import { InventoryResponse, EconomyResponse } from '@models/economy';
import { GameConfig } from '@models/game-config.interface';
import { SceneKey } from '@models/scene-keys';
import GameState from '@services/core/GameState';
import { LayoutService } from '@services/ui/LayoutService';
import { ApiService } from '@services/api/ApiService';
import { GameConfigService } from '@services/core/GameConfigService';
import { TranslationService } from '@services/core/TranslationService';
import { DPR } from '@constants/common.const';
import { Api } from '@services/api/api.interface';
import { loadThemeAssets } from '@utils/theme-assets.utils';
import { Spinner } from '../ui/Spinner';

export default class Preload extends Phaser.Scene {
  private spinner!: Spinner;
  private layout!: LayoutService;
  private api!: Api;
  private configService!: GameConfigService;

  private splashLogo?: Phaser.GameObjects.Image;
  private gameLogo!: Phaser.GameObjects.Image;
  private gameState!: GameState;

  constructor() {
    super(SceneKey.Preload);
  }

  preload() {
    // Solid background for the whole preload screen
    // No theme css variables at this stage
    this.cameras.main.setBackgroundColor('#2F44FE');

    this.api = new ApiService();
    this.gameState = new GameState();
    this.configService = new GameConfigService();
    this.layout = new LayoutService(this.scale);
  }

  create() {
    this.initLayout();
    this.initLoader();
    this.initGameData(SceneKey.Home);
  }

  private initLoader(): void {
    const y = this.gameLogo
      ? this.gameLogo.getBounds().bottom + 32 * DPR
      : this.layout.centerY();

    this.spinner = new Spinner(this, this.layout.centerX(), y, {
      color: 0xffffff,
    });
    this.add.existing(this.spinner);
    this.spinner.start();
  }

  private initLayout(): void {
    const cx = this.layout.centerX();
    const gap = 16 * DPR;

    // Game logo is loaded from the config URL — not available at preload time.
    // The spinner falls back to centerY when gameLogo is absent.
    const logoWidth = 108 * DPR;
    const logoY = this.scale.height - gap;

    this.splashLogo = this.add
      .image(cx, logoY, 'splash-logo')
      .setOrigin(0.5, 1);
    const scale = logoWidth / this.splashLogo.width;
    this.splashLogo.setScale(scale);
    const textY = logoY - this.splashLogo.height * 2 - gap;

    this.add
      .text(cx, textY, 'Powered by', {
        fontFamily: 'Korolev, sans-serif',
        fontSize: `${10 * DPR}px`,
        fontStyle: '500',
        color: '#FFFFFF',
        align: 'center',
        resolution: DPR,
      })
      .setOrigin(0.5, 1);
  }

  private async initGameData(sceneId: string) {
    try {
      const gameResponse = this.configService.gameResponse;
      if (!gameResponse) {
        this.scene.start(SceneKey.Error);
        return;
      }

      const gameConfig = await this.api.fetchGameConfig(
        gameResponse.gameConfigUrl,
        gameResponse.fallbackGameConfigUrl,
      );
      if (!gameConfig.game || !gameConfig.stage) {
        this.scene.start(SceneKey.Error);
        return;
      }
      const labelsUrl = gameResponse.gameLabelsUrl;
      const labels = await this.api.fetchGameLabels(
        labelsUrl,
        gameResponse.fallbackLabelsUrl,
      );

      const inventory = await this.api.fetchInventory();
      const economy = await this.api.fetchEconomy();
      this.setData(gameConfig, inventory, economy, labels);

      loadThemeAssets(this, gameConfig.stage);

      this.load.on('loaderror', (file: Phaser.Loader.File) => {
        console.error('[Preload] Failed to load asset:', file.key, file.url);
      });

      this.load.once('complete', () => {
        this.spinner?.destroy();
        this.scene.start(sceneId);
      });
      this.load.start();
    } catch (error) {
      console.error(error);
      this.scene.start(SceneKey.Error, {
        error,
      });
    }
  }

  private setData(
    game: GameConfig,
    inventory: InventoryResponse,
    economy: EconomyResponse,
    labels: Record<string, string>,
  ): void {
    const translationService = new TranslationService();
    translationService.setLabels(labels);

    this.configService.setGameConfig(game);
    this.gameState.initCollectibles(
      this.configService.collectibles,
      inventory.items,
    );
    this.gameState.syncEconomy(this.configService.actions, economy);
  }

}
